/**
 * Upload. One file goes to `POST /v1/documents`, several to `/batch` — and the batch
 * endpoint is deliberately all-or-nothing, so a rejected file rejects the whole drop.
 * The message names the file and the reason, because "upload failed" is not actionable.
 */
import { useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ApiError, post, upload } from "../lib/api";
import { BODY_MARGIN, packBatches } from "../lib/batches";
import { bytes } from "../lib/format";
import { useSession } from "../hooks/useSession";
import type { BulkResult, Limits } from "../lib/types";
import { Button } from "./ui";

const FORMATS = "pdf · xlsx · xls · docx · pptx · ods · odt · epub · csv · html · eml · rtf · txt";

/** What `POST /v1/documents` and `/batch` answer with, narrowed to the ids this needs. */
type Accepted = { document_id?: string; documents?: { document_id: string }[] };

/** One batch that did not go through, and why. Never collapsed into "some failed". */
interface BatchFailure {
  files: string[];
  reason: string;
}

/** Used only when the server did not report its own — an older build, or a failed probe. */
const FALLBACK_LIMITS: Limits = {
  max_upload_bytes: 64 * 1024 * 1024,
  max_batch_files: 20,
  max_bulk_ids: 200,
};

export function Upload({ collectionId }: { collectionId?: string } = {}) {
  const input = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [failures, setFailures] = useState<BatchFailure[]>([]);
  const queryClient = useQueryClient();
  const { me } = useSession();
  const limits = me?.limits ?? FALLBACK_LIMITS;

  const send = useMutation({
    mutationFn: async (files: File[]) => {
      // The drop is split against the server's own two ceilings rather than refused: a
      // person who selected 1500 files wants 1500 uploaded, and the count is rarely what
      // stops them — the byte cap on one request body is.
      const { batches, oversized } = packBatches(
        files,
        (file) => file.size,
        limits.max_batch_files,
        Math.floor(limits.max_upload_bytes * BODY_MARGIN),
      );

      const failed: BatchFailure[] = oversized.map(({ index, size }) => ({
        files: [files[index]?.name ?? `file ${index + 1}`],
        reason: `${bytes(size)} is larger than this server accepts in one request (${bytes(
          limits.max_upload_bytes,
        )}).`,
      }));

      const total = files.length - oversized.length;
      setProgress({ done: 0, total });
      let done = 0;

      for (const batch of batches) {
        let accepted: Accepted;
        try {
          // `done` stays whole — it is what the summary reports. Only the bar reads the
          // in-flight batch, so a single-request drop still moves while its bytes go out.
          accepted = await upload<Accepted>(
            batch.length === 1 ? "/v1/documents" : "/v1/documents/batch",
            batch,
            (fraction) => setProgress({ done: done + batch.length * fraction, total }),
          );
        } catch (error) {
          // A batch is all-or-nothing server-side, so one unsupported file costs its own
          // batch and nothing else. Keep going: the remaining batches are unaffected, and
          // stopping here would strand files the server would have taken.
          failed.push({
            files: batch.map((file) => file.name),
            reason:
              error instanceof ApiError && error.status === 413
                ? "larger than this server accepts in one request"
                : error instanceof Error
                  ? error.message
                  : String(error),
          });
          continue;
        }

        // Counted here, before the collection step: these documents exist and are
        // queued. Counting them after would report an upload that plainly happened as
        // "0 of 1549 accepted" when only the membership call failed.
        done += batch.length;
        setProgress({ done, total });

        // Membership is a second call rather than an upload parameter: the ingest
        // endpoint takes bytes and nothing else, and `POST /v1/documents/bulk` already
        // applies `add_collections` to a set of ids. Parsing is queued by the upload
        // either way, so the document is in the collection well before its run finishes.
        if (!collectionId) continue;
        const ids = accepted.documents
          ? accepted.documents.map((one) => one.document_id)
          : accepted.document_id
            ? [accepted.document_id]
            : [];

        // Chunked against max_bulk_ids, which is NOT max_batch_files: the bulk endpoint
        // caps its id list far lower than the batch endpoint caps its files, so a full
        // upload batch is several bulk calls.
        const idChunks = packBatches(ids, () => 1, limits.max_bulk_ids, limits.max_bulk_ids);
        for (const chunk of idChunks.batches) {
          try {
            await post<BulkResult>("/v1/documents/bulk", {
              ids: chunk,
              action: "add_collections",
              collections: [collectionId],
            });
          } catch (error) {
            // The files are uploaded and parsing either way — say that, rather than
            // implying they were lost.
            failed.push({
              files: [`${chunk.length} uploaded file${chunk.length === 1 ? "" : "s"}`],
              reason: `uploaded and queued, but not added to this collection: ${
                error instanceof Error ? error.message : String(error)
              }`,
            });
          }
        }
      }

      return { uploaded: done, failed, total: files.length };
    },
    onSuccess: (result) => {
      setProgress(null);
      setFailures(result.failed);
      const skipped = result.total - result.uploaded;
      setNote(
        `${result.uploaded} of ${result.total} file${result.total === 1 ? "" : "s"} accepted and queued${
          collectionId ? " into this collection" : ""
        }${skipped > 0 ? `; ${skipped} not taken` : ""}.`,
      );
      void queryClient.invalidateQueries({ queryKey: ["documents"] });
      void queryClient.invalidateQueries({ queryKey: ["collections"] });
      void queryClient.invalidateQueries({ queryKey: ["jobs"] });
    },
    onError: (error) => {
      setProgress(null);
      setNote(error instanceof Error ? error.message : String(error));
    },
  });

  function take(list: FileList | null) {
    const files = Array.from(list ?? []);
    if (files.length === 0) return;
    setNote(null);
    setFailures([]);
    send.mutate(files);
  }

  return (
    <div className="flex flex-col gap-2">
      <div
        onDragOver={(event) => {
          event.preventDefault();
          setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={(event) => {
          event.preventDefault();
          setOver(false);
          take(event.dataTransfer.files);
        }}
        className={`flex flex-wrap items-center gap-4 border border-dashed px-4 py-4 transition-colors duration-200 ease-alt ${
          over ? "border-gold bg-gold-wash" : "border-hair-strong bg-surface"
        }`}
      >
        <span className="grid size-10 shrink-0 place-items-center border border-hair text-gold">
          <svg
            viewBox="0 0 24 24"
            className="size-5"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="square"
          >
            <path d="M12 15V3m0 0L8 7m4-4 4 4" />
            <path d="M4 17v3h16v-3" />
          </svg>
        </span>
        <div className="min-w-0">
          <p className="font-mono text-[11.5px] font-bold tracking-[0.16em] uppercase">
            drop documents
          </p>
          <p className="mt-1 font-mono text-[10.5px] tracking-[0.06em] text-faint">{FORMATS}</p>
          <p className="mt-1 text-[11.5px] text-dim">
            Drop as many as you like — they are split into requests this server accepts
            ({limits.max_batch_files} files or {bytes(limits.max_upload_bytes)} each, whichever
            comes first). Every file is sniffed before anything is stored, so a renamed
            executable is refused rather than parsed.
          </p>
        </div>
        <div className="ms-auto flex shrink-0 items-center gap-2">
          <input
            ref={input}
            type="file"
            multiple
            aria-label="choose files"
            className="hidden"
            onChange={(event) => take(event.target.files)}
          />
          <Button variant="primary" onClick={() => input.current?.click()} disabled={send.isPending}>
            {send.isPending ? "uploading…" : "choose files"}
          </Button>
        </div>
      </div>

      {progress && (
        <div className="flex items-center gap-3 border border-hair bg-surface px-3 py-2">
          <div className="h-1 min-w-0 flex-1 bg-hair">
            <div
              className="h-1 bg-gold transition-[width] duration-200 ease-alt"
              style={{
                width: `${progress.total === 0 ? 0 : Math.round((progress.done / progress.total) * 100)}%`,
              }}
            />
          </div>
          <span className="tnum shrink-0 font-mono text-[10.5px] tracking-[0.14em] text-faint uppercase">
            {Math.floor(progress.done)} / {progress.total}
          </span>
        </div>
      )}

      {failures.length > 0 && (
        <ul className="border border-crit/40 bg-crit-wash px-3 py-2">
          {failures.map((failure) => (
            <li key={failure.files.join("|")} className="font-mono text-[11px] text-crit">
              &gt; {failure.files.length === 1
                ? failure.files[0]
                : `${failure.files.length} files`}{" "}
              — {failure.reason}
            </li>
          ))}
        </ul>
      )}

      {note && (
        <p
          className={`border px-3 py-2 font-mono text-[11.5px] ${
            send.isError || failures.length > 0
              ? "border-crit/40 bg-crit-wash text-crit"
              : "border-ok/40 bg-ok-wash text-ok"
          }`}
        >
          &gt; {note}
        </p>
      )}
    </div>
  );
}
